import React from "react";
import { useFetch } from "../lib/useApi";
import { Loader } from "./PageHead";

/** SPT-PTE-2026-0046 (2nd follow-up) — "Workload by Job" for the Job Flow
 *  Board's dashboard panel: fixed 7-discipline list (Dimensional, Pressure,
 *  Torque, Electronics, Temperature, Hydraulics, Mass), each bar STACKED by
 *  technician, colours generated dynamically (not hard-coded to any named
 *  person) and kept consistent for the same technician across every
 *  discipline row AND the "Technicians" legend below. Unassigned items are
 *  a fixed grey segment, always last. Counts individual job/calibration
 *  line items (same source of truth as every other workload widget:
 *  job_items.technician_name), not delivery notes. */
const PALETTE = ["#2f6fed", "#1ea672", "#8b5cf6", "#f59e0b", "#ec4899", "#06b6d4", "#ef4444", "#6366f1", "#84cc16", "#f97316"];
const UNASSIGNED_COLOR = "#9aa3ab";

// `colorForTech`/`onDisciplineClick`/`activeDiscipline` are optional — the
// Job Flow Board's own usage (no props passed) is byte-identical to before.
// The Technical Dashboard passes its own colour rule + row-click filter.
export function WorkloadByJobChart({ colorForTech, onDisciplineClick, activeDiscipline, title, subtitle, inLabOnly }) {
  const { data, loading } = useFetch("/job-flow/workload-by-discipline", inLabOnly ? { scope: "in_lab" } : undefined);
  const disciplines = data?.disciplines || [];
  const technicians = data?.technicians || [];
  const maxTotal = Math.max(1, ...disciplines.map((d) => d.total));
  const namedTechs = technicians.filter((t) => t.technician !== "Unassigned");

  const colorOf = (tech) => {
    if (colorForTech) return colorForTech(tech, technicians.map((t) => t.technician));
    if (tech === "Unassigned") return UNASSIGNED_COLOR;
    const idx = namedTechs.findIndex((t) => t.technician === tech);
    return PALETTE[idx >= 0 ? idx % PALETTE.length : 0];
  };

  return (
    <div className="card pad" data-testid="workload-by-job-chart-card">
      <div className="sec-h">
        <h3>{title || "Workload by job"}</h3>
        <span className="tag-soft">{subtitle || "By technician"}</span>
      </div>
      {loading ? <Loader /> : (
        <>
          {disciplines.map((d) => {
            const widthPct = (d.total / maxTotal) * 100;
            const clickable = !!onDisciplineClick;
            return (
              <div
                className="bar-row" key={d.discipline} data-testid={`wbj-row-${d.discipline}`}
                onClick={clickable ? () => onDisciplineClick(d.discipline) : undefined}
                style={clickable ? {
                  cursor: "pointer", borderRadius: 8, padding: "4px 6px", margin: "-4px -6px",
                  background: activeDiscipline === d.discipline ? "var(--card-2)" : "transparent",
                } : undefined}
              >
                <div className="nm" style={{ color: "var(--text)" }}>{d.discipline}</div>
                <div className="bar-track">
                  <div style={{ display: "flex", height: "100%", width: `${widthPct}%` }}>
                    {d.by_technician.map((seg) => (
                      <div
                        key={seg.technician}
                        style={{ width: `${(seg.count / (d.total || 1)) * 100}%`, height: "100%", background: colorOf(seg.technician) }}
                        title={`${seg.technician}: ${seg.count}`}
                        data-testid={`wbj-seg-${d.discipline}-${seg.technician}`}
                      />
                    ))}
                  </div>
                </div>
                <div className="vv">{d.total}</div>
              </div>
            );
          })}
          {technicians.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 28, marginTop: 18, paddingTop: 16, borderTop: "1px solid var(--line)" }}
              data-testid="wbj-technician-legend">
              <div style={{ width: "100%", color: "var(--gold-deep)", fontWeight: 700, fontSize: 13.5, marginBottom: 2 }}>Technicians</div>
              {technicians.map((t) => (
                <div key={t.technician} style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 90 }} data-testid={`wbj-legend-${t.technician}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 13, height: 13, borderRadius: "50%", background: colorOf(t.technician), flex: "none" }} />
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{t.technician}</span>
                  </div>
                  <div className="mono" style={{ fontWeight: 800, fontSize: 17 }}>{t.total}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
